import {
  type Draft,
  hasLevel,
  ladderAccess,
  MERGE_FIELD_OPTIONS,
  messageRates,
  messageTitle,
  notify,
  useCancelMessage,
  useComposer,
  useGraceAction,
  useGroups,
  useLists,
  useMessage,
  useMessageRecipients,
  usePeopleSearch,
  useRetryFailedRecipients,
} from '@church/data'
import type { EmailBlock } from '@church/domain'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import {
  faArrowDown,
  faArrowRight,
  faArrowUp,
  faClock,
  faEnvelope,
  faFloppyDisk,
  faHandPointer,
  faHeading,
  faHorizontalRule,
  faMessage,
  faParagraph,
  faPaperPlane,
  faRotateRight,
  faStop,
  faTrashCan,
  faXmark,
} from '@church/ui-icons'
import { StatusPill } from '../components/messaging/MessagingPanels.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  ListTitle,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  InlineSpinner,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'

/** One message: the composer while it is a draft, its results once it is sent. */
export const Route = createFileRoute('/_authed/_shell/messages/$id')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'messages', 'viewer')) throw redirect({ to: '/' })
  },
  component: MessageScreen,
})

function MessageScreen() {
  const { id } = Route.useParams()
  const message = useMessage(id === 'new' ? null : id)
  if (id === 'new') return <Composer />
  if (message.isPending) return <Spinner />
  if (!message.data) return <ErrorNote error={message.error} />
  const m = message.data
  if (m.status === 'draft' || m.status === 'scheduled')
    return (
      <Composer
        existing={{
          id: m.id,
          status: m.status,
          channel: m.channel,
          subject: m.subject ?? '',
          blocks: m.blocks,
          audienceKind: m.audienceKind,
          listId: m.listId,
          groupId: m.groupId,
        }}
        scheduledAt={m.scheduledAt}
      />
    )
  return <Report id={m.id} />
}

// ---- composer -------------------------------------------------------------------------

function Composer({
  existing,
  scheduledAt,
}: {
  existing?: Draft & { id: string; status: string }
  scheduledAt?: string | null
}) {
  const c = useComposer(existing)
  const lists = useLists()
  const groups = useGroups()
  const [focused, setFocused] = useState(0)
  const [later, setLater] = useState(scheduledAt ? scheduledAt.slice(0, 16) : '')
  // Ten seconds between Send and sending, to take it back.
  const grace = useGraceAction(10)
  const reach = (c.audience?.reachable ?? 0).toLocaleString()
  const [previewQuery, setPreviewQuery] = useState('')
  const search = usePeopleSearch(previewQuery)
  const sent = c.send.data && c.send.data.status !== 'draft'
  useBreadcrumbs([{ label: existing ? 'Edit message' : 'New message' }])

  const insertField = (token: string) => {
    if (c.draft.channel === 'sms') return c.setSmsBody(`${c.smsBody}${token}`)
    const block = c.draft.blocks[focused]
    if (block && 'text' in block) c.setBlock(focused, { ...block, text: `${block.text}${token}` })
  }

  if (sent && c.id) {
    return (
      <div className="space-y-6">
        <PageHeader
          visible
          title={c.send.data?.status === 'scheduled' ? 'Scheduled' : 'On its way'}
        />
        <Card className="space-y-2 p-6">
          <p className="text-content-default">
            {c.send.data?.status === 'scheduled'
              ? `It will go out ${new Date(c.send.data.scheduledAt ?? '').toLocaleString()}.`
              : 'Sending in the background — you can leave this page.'}
          </p>
          <Link
            to="/messages/$id"
            params={{ id: c.id }}
            className="inline-flex items-center gap-2 text-sm text-content-brand"
            reloadDocument
          >
            Watch the results
            <Icon icon={faArrowRight} />
          </Link>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader title={existing ? 'Edit message' : 'New message'} />
      <div className="grid gap-6 xl:grid-cols-5">
        {/* min-w-0: a date input's intrinsic width must not stretch a phone's column. */}
        <div className="min-w-0 space-y-4 xl:col-span-3">
          <Card className="space-y-4 p-4">
            <div className="flex flex-wrap gap-4">
              <div
                role="radiogroup"
                aria-label="Channel"
                className="flex rounded-lg border border-border-default p-0.5"
              >
                {(['email', 'sms'] as const).map((channel) => (
                  <button
                    key={channel}
                    role="radio"
                    aria-checked={c.draft.channel === channel}
                    onClick={() => c.setChannel(channel)}
                    className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium ${
                      c.draft.channel === channel
                        ? 'bg-brand-solid text-brand-on-solid'
                        : 'text-content-muted'
                    }`}
                  >
                    <Icon icon={channel === 'email' ? faEnvelope : faMessage} />
                    {channel === 'email' ? 'Email' : 'Text message'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="To">
                <Select
                  value={c.draft.audienceKind}
                  onChange={(event) => c.setAudience(event.target.value as Draft['audienceKind'])}
                >
                  <option value="everyone">Everyone in the directory</option>
                  <option value="list">A saved list</option>
                  <option value="group">A group</option>
                </Select>
              </Field>
              {c.draft.audienceKind === 'list' && (
                <Field label="List">
                  <Select
                    value={c.draft.listId ?? ''}
                    onChange={(event) => c.setAudience('list', event.target.value || null)}
                  >
                    <option value="">Choose a list…</option>
                    {(lists.data ?? []).map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {c.draft.audienceKind === 'group' && (
                <Field label="Group">
                  <Select
                    value={c.draft.groupId ?? ''}
                    onChange={(event) => c.setAudience('group', event.target.value || null)}
                  >
                    <option value="">Choose a group…</option>
                    {groups.groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
            <AudienceCount
              audience={c.audience}
              loading={c.audienceLoading}
              channel={c.draft.channel}
            />
          </Card>

          <Card className="space-y-4 p-4">
            {c.draft.channel === 'email' && (
              <Field label="Subject">
                <Input
                  value={c.draft.subject}
                  onChange={(event) => c.setSubject(event.target.value)}
                  placeholder="Christmas at Grace"
                />
              </Field>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-content-muted">Insert:</span>
              {MERGE_FIELD_OPTIONS.map((field) => (
                <button
                  key={field.token}
                  onClick={() => insertField(field.token)}
                  className="rounded-full border border-border-default px-2 py-0.5 text-content-default hover:bg-surface-sunken"
                >
                  {field.label}
                </button>
              ))}
              <span className="text-content-subtle">
                Missing values fall back safely (“friend”).
              </span>
            </div>
            {c.draft.channel === 'sms' ? (
              <Field label="Text">
                <Textarea
                  rows={5}
                  value={c.smsBody}
                  onChange={(event) => c.setSmsBody(event.target.value)}
                />
              </Field>
            ) : (
              <BlockEditor
                blocks={c.draft.blocks}
                onFocus={setFocused}
                onChange={c.setBlock}
                onMove={c.moveBlock}
                onRemove={c.removeBlock}
                onAdd={c.addBlock}
              />
            )}
            {c.segments && (
              <p
                className={`text-sm ${c.segments.segments > 2 ? 'text-warning-content' : 'text-content-muted'}`}
              >
                {c.segments.units} characters including the opt-out line · {c.segments.segments}{' '}
                message
                {c.segments.segments > 1 ? 's' : ''} each ({c.segments.encoding})
                {c.segments.encoding === 'UCS-2' &&
                  ' — an emoji or curly quote halves the length of every message'}
              </p>
            )}
            {c.unknownFields.length > 0 && (
              <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-content">
                Unknown field{c.unknownFields.length > 1 ? 's' : ''}:{' '}
                {c.unknownFields.map((f) => `{{${f}}}`).join(', ')}. Use the Insert buttons above.
              </p>
            )}
          </Card>

          <Card className="flex flex-wrap items-end gap-3 p-4">
            <Button
              variant="secondary"
              icon={faFloppyDisk}
              onClick={() => c.save.mutate()}
              disabled={c.save.isPending}
            >
              {c.save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
            {grace.left !== null ? (
              <div
                role="status"
                className="flex items-center gap-3 rounded-control bg-brand-soft px-3 py-2 text-sm text-content-brand"
              >
                <InlineSpinner />
                Sending to {reach} in {grace.left}s
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (grace.undo())
                      notify({
                        title: 'Not sent',
                        description: 'It’s still a draft, unchanged.',
                        tone: 'info',
                      })
                  }}
                >
                  Undo
                </Button>
              </div>
            ) : (
              <ConfirmButton
                label="Send now"
                icon={faPaperPlane}
                variant="primary"
                disabled={!c.canSend || c.send.isPending}
                title={`Send to ${c.audience?.reachable.toLocaleString() ?? 0} people?`}
                description={
                  c.audience && c.audience.unsubscribed + c.audience.noAddress > 0
                    ? `${(c.audience.unsubscribed + c.audience.noAddress).toLocaleString()} in this audience will not receive it (opted out or no ${c.draft.channel === 'email' ? 'email' : 'mobile number'}).`
                    : 'It goes out in the background at your provider’s pace.'
                }
                confirmLabel="Send"
                onConfirm={() =>
                  grace.start(() =>
                    c.send.mutate(null, {
                      onSuccess: () =>
                        notify({
                          title: `Sending to ${reach} people`,
                          description: 'It goes out in the background; the results fill in here.',
                        }),
                    }),
                  )
                }
              />
            )}
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1 basis-56">
                <Field label="Or send later">
                  <Input
                    type="datetime-local"
                    value={later}
                    onChange={(event) => setLater(event.target.value)}
                  />
                </Field>
              </div>
              <Button
                variant="secondary"
                disabled={!c.canSend || !later || c.send.isPending}
                onClick={() => c.send.mutate(new Date(later).toISOString())}
                icon={faClock}
              >
                Schedule
              </Button>
            </div>
            {c.save.isSuccess && !c.save.isPending && (
              <span className="text-sm text-success-content">Saved.</span>
            )}
          </Card>
          <ErrorNote error={c.save.error ?? c.send.error} />
        </div>

        <div className="min-w-0 space-y-3 xl:col-span-2">
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Field label="Preview as">
                <Input
                  value={previewQuery}
                  onChange={(event) => setPreviewQuery(event.target.value)}
                  placeholder="Search a person…"
                />
              </Field>
            </div>
            {c.previewPersonId && (
              <IconButton
                icon={faXmark}
                label="Preview without a person"
                onClick={() => c.setPreviewPersonId(null)}
              />
            )}
          </div>
          {previewQuery.length >= 2 && search.results.length > 0 && !c.previewPersonId && (
            <Card className="divide-y divide-border-subtle text-sm">
              {search.results.slice(0, 5).map((person) => (
                <button
                  key={person.id}
                  onClick={() => {
                    c.setPreviewPersonId(person.id)
                    setPreviewQuery(`${person.firstName} ${person.lastName}`)
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-surface-sunken"
                >
                  {person.firstName} {person.lastName}
                </button>
              ))}
            </Card>
          )}
          <Preview preview={c.preview} />
          <ErrorNote error={c.previewError} />
        </div>
      </div>
    </div>
  )
}

function AudienceCount({
  audience,
  loading,
  channel,
}: {
  audience: { total: number; reachable: number; unsubscribed: number; noAddress: number } | null
  loading: boolean
  channel: 'email' | 'sms'
}) {
  if (!audience)
    return (
      <p className="text-sm text-content-muted">
        {loading ? 'Counting…' : 'Choose who it goes to.'}
      </p>
    )
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg bg-surface-sunken px-3 py-2 text-sm"
      aria-live="polite"
    >
      <span>
        <span className="text-lg font-semibold tabular-nums text-content-default">
          {audience.reachable.toLocaleString()}
        </span>{' '}
        <span className="text-content-muted">will receive it</span>
      </span>
      {audience.unsubscribed > 0 && (
        <span className="text-content-muted">
          {audience.unsubscribed.toLocaleString()} opted out
        </span>
      )}
      {audience.noAddress > 0 && (
        <span className="text-content-muted">
          {audience.noAddress.toLocaleString()} without{' '}
          {channel === 'email' ? 'an email' : 'a mobile number'}
        </span>
      )}
      {loading && <span className="text-content-subtle">updating…</span>}
    </div>
  )
}

function BlockEditor({
  blocks,
  onFocus,
  onChange,
  onMove,
  onRemove,
  onAdd,
}: {
  blocks: EmailBlock[]
  onFocus: (index: number) => void
  onChange: (index: number, block: EmailBlock) => void
  onMove: (index: number, by: -1 | 1) => void
  onRemove: (index: number) => void
  onAdd: (block: EmailBlock) => void
}) {
  return (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <div
          key={index}
          className="group space-y-1 rounded-lg border border-border-subtle p-3"
          onFocus={() => onFocus(index)}
        >
          <div className="flex items-center justify-between text-xs text-content-subtle">
            <span className="uppercase tracking-wide">{block.type}</span>
            <span className="-my-2 -mr-2 flex">
              <IconButton icon={faArrowUp} label="Move up" onClick={() => onMove(index, -1)} />
              <IconButton icon={faArrowDown} label="Move down" onClick={() => onMove(index, 1)} />
              <IconButton
                icon={faTrashCan}
                label="Remove block"
                className="hover:text-danger-solid"
                onClick={() => onRemove(index)}
              />
            </span>
          </div>
          {block.type === 'heading' && (
            <Input
              aria-label="Heading"
              value={block.text}
              onChange={(event) => onChange(index, { ...block, text: event.target.value })}
              className="text-lg font-semibold"
            />
          )}
          {block.type === 'paragraph' && (
            <Textarea
              aria-label="Paragraph"
              rows={4}
              value={block.text}
              onChange={(event) => onChange(index, { ...block, text: event.target.value })}
            />
          )}
          {block.type === 'button' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                aria-label="Button label"
                value={block.text}
                onChange={(event) => onChange(index, { ...block, text: event.target.value })}
                placeholder="Register"
              />
              <Input
                aria-label="Button link"
                value={block.url}
                onChange={(event) => onChange(index, { ...block, url: event.target.value })}
                placeholder="https://"
              />
            </div>
          )}
          {block.type === 'divider' && <hr className="border-border-subtle" />}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          icon={faHeading}
          onClick={() => onAdd({ type: 'heading', text: '' })}
        >
          Heading
        </Button>
        <Button
          variant="secondary"
          icon={faParagraph}
          onClick={() => onAdd({ type: 'paragraph', text: '' })}
        >
          Paragraph
        </Button>
        <Button
          variant="secondary"
          icon={faHandPointer}
          onClick={() => onAdd({ type: 'button', text: 'Learn more', url: 'https://' })}
        >
          Button
        </Button>
        <Button
          variant="secondary"
          icon={faHorizontalRule}
          onClick={() => onAdd({ type: 'divider' })}
        >
          Divider
        </Button>
      </div>
    </div>
  )
}

function Preview({
  preview,
}: {
  preview: {
    channel: 'email' | 'sms'
    subject: string | null
    html: string | null
    text: string
  } | null
}) {
  if (!preview)
    return <Card className="p-6 text-sm text-content-muted">The preview appears as you write.</Card>
  if (preview.channel === 'sms')
    return (
      <Card className="mx-auto max-w-xs space-y-2 rounded-3xl p-4">
        <p className="text-center text-xs text-content-subtle">Text message</p>
        <div className="rounded-2xl bg-surface-sunken px-3 py-2 text-sm whitespace-pre-wrap text-content-default">
          {preview.text}
        </div>
      </Card>
    )
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border-subtle px-4 py-2 text-sm">
        <span className="text-content-muted">Subject: </span>
        <span className="font-medium text-content-default">{preview.subject}</span>
      </div>
      {/* Sandboxed: the preview is the real email HTML, shown inert. */}
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={preview.html ?? ''}
        className="h-[32rem] w-full bg-white"
      />
    </Card>
  )
}

// ---- report --------------------------------------------------------------------------

const RECIPIENT_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  sent: 'success',
  delivered: 'success',
  opened: 'success',
  clicked: 'success',
  queued: 'warning',
  pending: 'warning',
  failed: 'danger',
  bounced: 'danger',
}

const RECIPIENT_FILTERS = ['', 'sent', 'suppressed', 'failed', 'bounced'] as const

function Report({ id }: { id: string }) {
  const message = useMessage(id)
  const cancel = useCancelMessage()
  const retry = useRetryFailedRecipients()
  const [filter, setFilter] = useState<(typeof RECIPIENT_FILTERS)[number]>('')
  const recipients = useMessageRecipients(id, filter || undefined)
  const m = message.data
  useBreadcrumbs([{ label: m ? messageTitle(m) : 'Message' }])
  if (!m) return <Spinner />
  const s = m.stats
  const rates = messageRates(s)
  const done = s.sent + s.suppressed + s.failed
  const progress = s.recipients > 0 ? Math.round((done / s.recipients) * 100) : 0
  const title = messageTitle(m)
  const tiles: { label: string; value: string; hint?: string }[] = [
    {
      label: 'Delivered to',
      value: s.sent.toLocaleString(),
      hint: `of ${s.recipients.toLocaleString()} in the audience`,
    },
    ...(m.channel === 'email'
      ? [
          {
            label: 'Opened',
            value: `${rates.opened}%`,
            hint: `${s.opened.toLocaleString()} people`,
          },
          {
            label: 'Clicked',
            value: `${rates.clicked}%`,
            hint: `${s.clicked.toLocaleString()} people`,
          },
        ]
      : []),
    { label: 'Unsubscribed', value: s.unsubscribed.toLocaleString() },
    { label: 'Suppressed', value: s.suppressed.toLocaleString(), hint: 'opted out or no address' },
    { label: 'Failed / bounced', value: (s.failed + s.bounced).toLocaleString() },
  ]
  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        facts={`${m.channel === 'email' ? 'Email' : 'Text'} · ${new Date(m.startedAt ?? m.createdAt).toLocaleString()}`}
        action={
          <div className="flex items-center gap-3">
            <StatusPill status={m.status} />
            {(m.status === 'sending' || m.status === 'queued') && (
              <ConfirmButton
                label="Stop sending"
                icon={faStop}
                variant="secondary"
                title="Stop this message?"
                description="Whoever has not received it yet will not."
                confirmLabel="Stop"
                onConfirm={() => cancel.mutate(id)}
              />
            )}
            {m.status === 'sent' && s.failed > 0 && (
              <ConfirmButton
                label={`Retry ${s.failed.toLocaleString()} failed`}
                icon={faRotateRight}
                variant="secondary"
                title={`Send again to ${s.failed.toLocaleString()} ${s.failed === 1 ? 'person' : 'people'}?`}
                description="Only the recipients whose delivery failed — after an email or texting outage, say. Everyone who received it is left alone."
                confirmLabel="Send again"
                disabled={retry.isPending}
                onConfirm={() => retry.mutate(id)}
              />
            )}
          </div>
        }
      />
      {(m.status === 'sending' || m.status === 'queued') && (
        <Card className="space-y-2 p-4">
          <div className="flex justify-between text-sm">
            <span className="text-content-default">Sending at your provider’s pace…</span>
            <span className="tabular-nums text-content-muted">
              {done.toLocaleString()} / {s.recipients.toLocaleString()}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-sunken">
            <div
              className="h-full rounded-full bg-brand-solid transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {tiles.map((tile) => (
          <Card key={tile.label} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-content-subtle">
              {tile.label}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-content-default">
              {tile.value}
            </p>
            {tile.hint && <p className="text-xs text-content-muted">{tile.hint}</p>}
          </Card>
        ))}
      </div>
      <Card bleed>
        <ListTitle title="Recipients">
          <Select
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
            aria-label="Filter recipients"
          >
            {RECIPIENT_FILTERS.map((f) => (
              <option key={f} value={f}>
                {f ? f[0]?.toUpperCase() + f.slice(1) : 'Everyone'}
              </option>
            ))}
          </Select>
        </ListTitle>
        <ul className="max-h-96 divide-y divide-border-subtle overflow-y-auto">
          {(recipients.data ?? []).map((r) => (
            <li key={r.personId} className={`${listRow} flex items-center justify-between gap-3`}>
              <Link
                to="/people/$id"
                params={{ id: r.personId }}
                className="min-w-0 truncate text-content-default no-underline hover:text-content-brand"
              >
                {r.name}
              </Link>
              <span className="flex shrink-0 items-center gap-2 text-xs text-content-muted">
                {r.reason && (
                  <span className="hidden sm:inline">{r.reason.replaceAll('_', ' ')}</span>
                )}
                <Chip tone={RECIPIENT_TONE[r.status] ?? 'neutral'}>{r.status}</Chip>
              </span>
            </li>
          ))}
        </ul>
      </Card>
      <ErrorNote error={cancel.error ?? message.error} />
    </div>
  )
}
